import { Text, View } from "react-native";
import { COLORS } from "../../lib/brand";

/** Tier colors (match DESIGN_SYSTEM + lime for STRONG). */
const RED_WEAK = "#C0392B";
const AMBER_FAIR = COLORS.AMBER.hex;
const LIME_STRONG = "#A8C84F";
const GREEN_VERY = "#5FAD56";
const BAR_EMPTY = "rgba(28,43,26,0.12)";

/**
 * Acceptance rules — must ALL be true to create an account:
 *   1. ≥ 8 characters
 *   2. has an uppercase letter
 *   3. has a digit
 *   4. has a symbol (anything non-alphanumeric)
 *
 * `passwordMeetsMinimumStrength` is true only when all four are met (the meter
 * shows VERY STRONG). The lower tiers represent progress toward the four rules:
 * each rule satisfied counts as one bar.
 */
function ruleChecks(password: string): { length: boolean; upper: boolean; digit: boolean; symbol: boolean } {
  return {
    length: password.length >= 8,
    upper: /[A-Z]/.test(password),
    digit: /\d/.test(password),
    symbol: /[^A-Za-z0-9]/.test(password),
  };
}

function rulesPassedCount(password: string): 0 | 1 | 2 | 3 | 4 {
  if (!password) return 0;
  const r = ruleChecks(password);
  const passed = [r.length, r.upper, r.digit, r.symbol].filter(Boolean).length;
  return passed as 0 | 1 | 2 | 3 | 4;
}

export type PasswordStrengthLabel = "WEAK" | "FAIR" | "STRONG" | "VERY STRONG";

export type PasswordDisplayStrength = {
  /** How many of 4 bars are filled (0 = empty password UI only). */
  filled: 0 | 1 | 2 | 3 | 4;
  label: PasswordStrengthLabel | null;
  /** Color for filled segments and label. */
  accent: string;
};

/** One bar per acceptance rule met: 1 red WEAK, 2 amber FAIR, 3 lime STRONG, 4 green VERY STRONG. */
export function passwordStrengthDisplay(password: string): PasswordDisplayStrength {
  const passed = rulesPassedCount(password);
  if (passed === 0) return { filled: 0, label: null, accent: BAR_EMPTY };
  if (passed === 1) return { filled: 1, label: "WEAK", accent: RED_WEAK };
  if (passed === 2) return { filled: 2, label: "FAIR", accent: AMBER_FAIR };
  if (passed === 3) return { filled: 3, label: "STRONG", accent: LIME_STRONG };
  return { filled: 4, label: "VERY STRONG", accent: GREEN_VERY };
}

function segmentColor(index: number, filled: number, accent: string): string {
  if (index >= filled) return BAR_EMPTY;
  return accent;
}

const BAR_INDICES = [0, 1, 2, 3];

export function PasswordStrengthMeter({ password }: { password: string }) {
  if (!password) {
    return (
      <View className="mt-2">
        <View className="flex-row gap-1">
          {BAR_INDICES.map((i) => (
            <View key={i} className="h-[3px] flex-1 rounded-sm" style={{ backgroundColor: BAR_EMPTY }} />
          ))}
        </View>
      </View>
    );
  }

  const { filled, label, accent } = passwordStrengthDisplay(password);

  return (
    <View className="mt-2">
      <View className="flex-row gap-1">
        {BAR_INDICES.map((i) => (
          <View key={i} className="h-[3px] flex-1 rounded-sm" style={{ backgroundColor: segmentColor(i, filled, accent) }} />
        ))}
      </View>
      {label ? (
        <Text
          className="mt-1 min-w-[120px] text-right text-[9px] font-bold uppercase tracking-wide"
          style={{ fontFamily: "DMSans_700Bold", color: accent }}
        >
          {label}
        </Text>
      ) : null}
    </View>
  );
}

/**
 * Signup acceptance gate. The user must hit VERY STRONG — every rule satisfied:
 * ≥ 8 chars, an uppercase, a digit, a symbol.
 *
 * The rules and the user-facing hint ("8+ characters, including uppercase,
 * digit, and symbol") must stay in sync — change one and you must change both.
 */
export function passwordMeetsMinimumStrength(password: string): boolean {
  return rulesPassedCount(password) === 4;
}
